import type { Locale as DateFnsLocale } from 'date-fns';
import type { QuasarLanguage } from 'quasar';
import { enUS, es, vi, zhCN } from 'date-fns/locale';
import type { AppLocale } from './index';

/**
 * Gói ngôn ngữ của THƯ VIỆN, tách khỏi message của app.
 *
 * `i18n.locale` chỉ chi phối chuỗi do ta viết. Chuỗi do Quasar tự vẽ (bảng "No data available",
 * tên tháng/thứ ở date picker, "Records per page", nút phân trang) và định dạng ngày của
 * date-fns đi theo hai bộ RIÊNG — không lật chúng cùng lúc là app nửa Việt nửa Anh.
 *
 * Gói Quasar nạp LAZY (mỗi gói ~3KB: en-US 3004 · es 3213 · vi 3401 · zh-CN 3142 byte), nên
 * `loadQuasarLang` là `async`. Chính vì có `await` đó mà `setI18nLanguage` phải nạp gói TRƯỚC
 * cổng `seq` và trong `try` — đặt sau cổng là mở lại cuộc đua, đặt ngoài `try` là chunk hỏng
 * thì hàm ném thay vì trả `false`.
 */
const quasarPacks: Record<AppLocale, () => Promise<{ default: QuasarLanguage }>> = {
  'en': () => import('quasar/lang/en-US'),
  'es': () => import('quasar/lang/es'),
  'vi': () => import('quasar/lang/vi'),
  'zh': () => import('quasar/lang/zh-CN'),
};

export const loadQuasarLang = async (
  locale: AppLocale,
): Promise<QuasarLanguage> => (await quasarPacks[locale]()).default;

const dateFnsLocales: Record<AppLocale, DateFnsLocale> = {
  en: enUS,
  es,
  vi,
  zh: zhCN,
};

/**
 * date-fns không có state toàn cục — `format()` nhận `locale` theo từng lời gọi. Nên hàm này
 * là bản tra cứu thuần để `utils/dateUtil` dùng, và `dateUtil` vẫn KHÔNG import i18n (luật
 * `STRUCTURE.md`: util chỉ chứa function thuần, không đọc trạng thái app).
 *
 * Nhận `unknown` vì overload legacy của `formatDate` khai `legacyLocale?: string | unknown`;
 * mã không nhận ra ⇒ `enUS`, khớp `fallbackLocale: 'en'` của `boot/i18n.ts`.
 */
export const getDateFnsLocale = (locale: unknown): DateFnsLocale =>
  dateFnsLocales[locale as AppLocale] ?? enUS;
